import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../components/Toast';

const AuthContext = createContext();

axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// The Google login callback redirects to /login?token=<jwt>. Take the token out of
// the address bar when this module loads, before the router, analytics or any
// component sees the URL. Other query parameters are kept.
function takeTokenFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const urlToken = url.searchParams.get('token');
    if (!urlToken) return null;
    url.searchParams.delete('token');
    window.history.replaceState(window.history.state, document.title, url.pathname + url.search + url.hash);
    localStorage.setItem('token', urlToken);
    localStorage.removeItem('isGuest');
    return urlToken;
  } catch (e) {
    return null;
  }
}

let redirectAfterUrlLogin = !!takeTokenFromUrl();

// Only drop the stored session when the server says the token is bad,
// not when the server is unreachable or waking up.
const isAuthFailure = (err) => [400, 401, 404].includes(err?.response?.status);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [isGuest, setIsGuest] = useState(
    () => !localStorage.getItem('token') && (localStorage.getItem('isGuest') === 'true' || !!localStorage.getItem('guestId'))
  );
  const [guestId, setGuestId] = useState(() => (localStorage.getItem('token') ? null : localStorage.getItem('guestId')));
  const [loading, setLoading] = useState(() => !!localStorage.getItem('token'));
  const navigate = useNavigate();

  const isAuthenticated = !!token || isGuest;
  const checkIsAuthenticated = useCallback(() => !!token || isGuest, [token, isGuest]);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!token) return null;
    try {
      const res = await axios.get('/api/auth/user', { headers: { 'x-auth-token': token } });
      setUser(res.data);
      return res.data;
    } catch (err) {
      if (isAuthFailure(err)) clearSession();
      return null;
    }
  }, [token, clearSession]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get('/api/auth/user', { headers: { 'x-auth-token': token } })
      .then((res) => {
        if (!cancelled) setUser(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isAuthFailure(err)) {
          clearSession();
          toast('Your session has expired. Please log in again.', { error: true });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, clearSession]);

  // Finish a Google login: the token was already stored when this module loaded.
  useEffect(() => {
    if (redirectAfterUrlLogin) {
      redirectAfterUrlLogin = false;
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // A guest who has a session flag but no id yet gets one.
  useEffect(() => {
    if (isGuest && !guestId) {
      const newGuestId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      localStorage.setItem('guestId', newGuestId);
      setGuestId(newGuestId);
    }
  }, [isGuest, guestId]);

  const login = async (usernameOrEmail, password) => {
    try {
      const res = await axios.post('/api/auth/login', { usernameOrEmail, password });
      localStorage.setItem('token', res.data.token);
      localStorage.removeItem('isGuest');
      setIsGuest(false);
      setToken(res.data.token);
      return { success: true };
    } catch (err) {
      if (err.response && err.response.data) {
        if (err.response.data.requiresVerification) {
          return {
            success: false,
            requiresVerification: true,
            message: err.response.data.msg,
            email: err.response.data.email
          };
        }
        const data = err.response.data;
        return { success: false, message: typeof data === 'string' ? data : data.msg || data.message };
      }
      throw err;
    }
  };

  const register = async (usernameOrData, email, password) => {
    try {
      let userData;
      if (typeof usernameOrData === 'object') {
        userData = { ...usernameOrData };
        if (userData.password) userData.password = userData.password.trim();
      } else {
        userData = {
          username: usernameOrData.trim(),
          email: email || '',
          password: password.trim()
        };
      }

      const res = await axios.post('/api/auth/register', userData);

      if (res.data && res.data.token && !res.data.requiresVerification) {
        localStorage.setItem('token', res.data.token);
        localStorage.removeItem('isGuest');
        localStorage.removeItem('guestId');
        setToken(res.data.token);
        setIsGuest(false);
        setGuestId(null);
      }

      return {
        success: true,
        emailSent: res.data.requiresVerification,
        message: res.data.message
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isGuest');
    localStorage.removeItem('guestId');
    setToken(null);
    setUser(null);
    setIsGuest(false);
    setGuestId(null);
    navigate('/');
  };

  const playAsGuest = () => {
    const existingGuestId = localStorage.getItem('guestId');
    const newGuestId = existingGuestId || Math.random().toString(36).substring(2);
    if (!existingGuestId) localStorage.setItem('guestId', newGuestId);
    setGuestId(newGuestId);
    setIsGuest(true);
  };

  const exitGuestMode = () => {
    localStorage.removeItem('isGuest');
    localStorage.removeItem('guestId');
    setIsGuest(false);
    setGuestId(null);
    navigate('/login');
  };

  const loginWithGoogle = async () => {
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    window.location.href = `${apiBaseUrl}/api/auth/google`;
    return { success: true };
  };

  const updateUsername = async (newUsername) => {
    try {
      const response = await axios.put(
        '/api/users/update-username',
        { newUsername },
        { headers: { 'x-auth-token': token } }
      );
      setUser((prev) => ({ ...prev, username: newUsername }));
      return { success: true, message: response.data.message };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to update username'
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isGuest,
        guestId,
        loading,
        isAuthenticated,
        checkIsAuthenticated,
        login,
        register,
        logout,
        playAsGuest,
        exitGuestMode,
        loginWithGoogle,
        fetchUserData,
        updateUsername
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
